import { useCallback, useEffect, useRef, useState } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { MapPin, Crosshair, Search, Loader2 } from "lucide-react";

/**
 * MapPicker — Google Maps address picker for checkout.
 *
 * Props:
 *   value: { lat, lng } | null — current pin position (controlled)
 *   defaultCenter: { lat, lng } — fallback centre when no pin yet
 *   onLocationChange({ lat, lng, address_line1, city, state, pincode, formatted_address, geo_source }): void
 *
 * Emits `onLocationChange` on: pin drag end, GPS lock, place-search select,
 * or a plain map tap. Never emits during typing.
 */

// A stable placeholder mapId lets AdvancedMarker render without forcing the
// merchant to create a Cloud-styled map — Google renders default styles.
const MAP_ID = process.env.REACT_APP_GOOGLE_MAP_ID;
const DEFAULT_CENTER = { lat: 28.686549, lng: 77.487345 }; // Govindpuram pickup

// India-restricted so autocomplete never wastes clicks on foreign addresses.
const COUNTRY_RESTRICTION = { country: "in" };

function extractAddressComponents(components) {
  const get = (type) =>
    components.find((c) => c.types.includes(type))?.long_name || "";
  const line1Parts = [
    get("street_number"),
    get("route"),
    get("premise"),
    get("subpremise"),
  ].filter(Boolean);
  const neighborhood = get("neighborhood") || get("sublocality_level_1") || get("sublocality");
  return {
    address_line1: (line1Parts.join(" ") || neighborhood || "").trim(),
    city: get("locality") || get("administrative_area_level_2") || "",
    state: get("administrative_area_level_1") || "",
    pincode: get("postal_code") || "",
  };
}

function GeocoderBridge({ position, source, onLocationChange }) {
  const geocodingLib = useMapsLibrary("geocoding");
  const geocoderRef = useRef(null);
  const lastKeyRef = useRef("");

  useEffect(() => {
    if (geocodingLib && !geocoderRef.current) {
      geocoderRef.current = new geocodingLib.Geocoder();
    }
  }, [geocodingLib]);

  useEffect(() => {
    if (!position || !geocoderRef.current) return;
    const key = `${position.lat.toFixed(6)},${position.lng.toFixed(6)}:${source}`;
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    geocoderRef.current.geocode(
      { location: position, region: "in" },
      (results, status) => {
        if (status !== "OK" || !results?.length) {
          onLocationChange({
            lat: position.lat,
            lng: position.lng,
            address_line1: "",
            city: "",
            state: "",
            pincode: "",
            formatted_address: "",
            geo_source: source,
          });
          return;
        }
        const best = results[0];
        onLocationChange({
          lat: position.lat,
          lng: position.lng,
          ...extractAddressComponents(best.address_components || []),
          formatted_address: best.formatted_address || "",
          geo_source: source,
        });
      }
    );
  }, [position, source, onLocationChange]);

  return null;
}

// Uses Places API (New): AutocompleteSuggestion + Place. The legacy
// places.Autocomplete widget is blocked on keys restricted to the new API.
function PlacesAutocomplete({ onPlaceSelect }) {
  const places = useMapsLibrary("places");
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const sessionRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!places) return;
    const q = query.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        if (!sessionRef.current) sessionRef.current = new places.AutocompleteSessionToken();
        const { suggestions: res } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: q,
          includedRegionCodes: [COUNTRY_RESTRICTION.country],
          sessionToken: sessionRef.current,
        });
        setSuggestions(res || []);
        setOpen(true);
      } catch (e) {
        console.error("Places autocomplete failed", e);
        setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, places]);

  const pick = useCallback(async (s) => {
    const place = s.placePrediction.toPlace();
    try {
      await place.fetchFields({ fields: ["location", "formattedAddress", "addressComponents", "displayName"] });
    } catch (e) {
      console.error("Place details failed", e);
      return;
    }
    sessionRef.current = null;
    setOpen(false);
    setQuery(place.formattedAddress || s.placePrediction.text?.text || "");
    if (!place.location) return;
    onPlaceSelect({
      lat: place.location.lat(),
      lng: place.location.lng(),
      components: (place.addressComponents || []).map((c) => ({ long_name: c.longText, short_name: c.shortText, types: c.types })),
      formatted_address: place.formattedAddress || place.displayName || "",
    });
  }, [onPlaceSelect]);

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-espresso-light pointer-events-none" />
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => suggestions.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid="map-search"
        placeholder="Search your address"
        autoComplete="off"
        className="w-full pl-10 pr-3 py-3 border border-line bg-ivory-100/40 focus:outline-none focus:border-gold text-espresso placeholder:text-espresso-light/70 text-sm"
      />
      {open && suggestions.length > 0 && (
        <ul data-testid="map-search-suggestions" className="absolute z-20 left-0 right-0 mt-1 bg-ivory border border-line shadow-lg max-h-64 overflow-auto">
          {suggestions.map((s, i) => {
            const p = s.placePrediction;
            return (
              <li key={p.placeId || i}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(s)}
                  data-testid={`map-suggestion-${i}`}
                  className="w-full text-left px-4 py-2.5 hover:bg-ivory-100 text-sm text-espresso flex items-start gap-2"
                >
                  <MapPin className="h-3.5 w-3.5 mt-0.5 text-gold-hover shrink-0" />
                  <span>
                    <span className="block">{p.mainText?.text || p.text?.text}</span>
                    {p.secondaryText?.text && <span className="block text-xs text-espresso-light">{p.secondaryText.text}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function GpsControl({ onLocate, locating }) {
  return (
    <button
      type="button"
      onClick={onLocate}
      disabled={locating}
      data-testid="map-locate-me"
      className="absolute top-3 right-3 z-10 bg-ivory border border-line px-3 py-2 flex items-center gap-2 text-xs text-espresso hover:bg-ivory-100 disabled:opacity-60 shadow-sm"
    >
      {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5 text-gold-hover" />}
      <span className="label-xs">{locating ? "Locating…" : "Use my location"}</span>
    </button>
  );
}

function MapBody({ value, defaultCenter, onLocationChange }) {
  const map = useMap();
  const [pin, setPin] = useState(value || null);
  const [pending, setPending] = useState(null); // { lat, lng, source }
  const [locating, setLocating] = useState(false);

  // Sync external value → local pin without reverse-geocoding.
  useEffect(() => {
    if (value && (value.lat !== pin?.lat || value.lng !== pin?.lng)) {
      setPin(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.lat, value?.lng]);

  const setPinAndGeocode = useCallback((lat, lng, source) => {
    setPin({ lat, lng });
    setPending({ lat, lng, source });
    if (map) map.panTo({ lat, lng });
  }, [map]);

  const handleMapClick = useCallback((e) => {
    if (!e?.detail?.latLng) return;
    const { lat, lng } = e.detail.latLng;
    setPinAndGeocode(lat, lng, "map_tap");
  }, [setPinAndGeocode]);

  const handleMarkerDragEnd = useCallback((e) => {
    const lat = e?.latLng?.lat?.();
    const lng = e?.latLng?.lng?.();
    if (typeof lat === "number" && typeof lng === "number") {
      setPinAndGeocode(lat, lng, "map_drag");
    }
  }, [setPinAndGeocode]);

  const handleLocateMe = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        setPinAndGeocode(pos.coords.latitude, pos.coords.longitude, "gps");
        if (map) map.setZoom(17);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
    );
  }, [map, setPinAndGeocode]);

  const handlePlaceSelect = useCallback((p) => {
    setPin({ lat: p.lat, lng: p.lng });
    setPending({ lat: p.lat, lng: p.lng, source: "search" });
    if (map) {
      map.panTo({ lat: p.lat, lng: p.lng });
      map.setZoom(17);
    }
  }, [map]);

  return (
    <div className="space-y-3">
      <PlacesAutocomplete onPlaceSelect={handlePlaceSelect} />
      <div className="relative border border-line overflow-hidden" style={{ height: 320 }}>
        <GpsControl onLocate={handleLocateMe} locating={locating} />
        <Map
          defaultCenter={value || defaultCenter || DEFAULT_CENTER}
          defaultZoom={value ? 17 : 13}
          mapId={MAP_ID}
          gestureHandling="greedy"
          disableDefaultUI={false}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          zoomControl={true}
          onClick={handleMapClick}
          style={{ width: "100%", height: "100%" }}
        >
          {pin && (
            <AdvancedMarker
              position={pin}
              draggable
              onDragEnd={handleMarkerDragEnd}
              data-testid="map-marker"
            >
              <div className="flex flex-col items-center pointer-events-none">
                <MapPin className="h-9 w-9 text-espresso drop-shadow-lg" fill="#C9A44E" strokeWidth={1.8} />
              </div>
            </AdvancedMarker>
          )}
          <GeocoderBridge
            position={pending}
            source={pending?.source}
            onLocationChange={(loc) => {
              setPending(null);
              onLocationChange(loc);
            }}
          />
        </Map>
      </div>
      {!pin && (
        <div className="text-xs text-espresso-light" data-testid="map-hint">
          Tap on the map, drag the pin, or use "Use my location" to set your exact drop spot for the rider.
        </div>
      )}
    </div>
  );
}

export default function MapPicker({ apiKey, value, defaultCenter, onLocationChange }) {
  if (!apiKey) {
    return (
      <div className="border border-dashed border-line p-4 text-xs text-espresso-light" data-testid="map-disabled">
        Map picker unavailable — Google Maps not configured. Please fill in the fields below manually.
      </div>
    );
  }
  return (
    <APIProvider apiKey={apiKey} libraries={["places", "geocoding"]}>
      <MapBody
        value={value}
        defaultCenter={defaultCenter}
        onLocationChange={onLocationChange}
      />
    </APIProvider>
  );
}
